/**
 * Taken from https://github.com/mantaskaveckas/react-siema and rewritten using react-hooks
 * Fixed errors and extended to support navigational dots.
 * TODO: Add to codex
 */
import { Children, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Icon } from '@codex/icons';

import CarouselNavigation from './CarouselNavigation/CarouselNavigation';

const ComponentWrapper = styled.div`
  position: relative;
`;
const CarouselWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  height: 100%;
  ${(props) => props.disabled && css`
    cursor: default;
    pointer-events: none;
    touch-action: none;
  `};
`;
const CarouselDisabledOverlay = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background :white;
    opacity: 0.85;
    z-index: 1;
`;

const SliderWrapper = styled.div`
  overflow: hidden;
  display: block;
  width: 100%;
  height: 100%;
`;

const SliderFrame = styled.div`
  height: 100%;
  
  * {
    // This corrects flickering during CSS transition/transformation in Safari
    -webkit-transform-style: preserve-3d;
    -webkit-backface-visibility: hidden;
  }

  // when dragging is in progress we have to disable clicking and touching
  // for case when there are some <a> elements in the content whichwould lead to redirection
  // to URL in href attribute
  ${(props) => props.draggingInProgress && css`
    a {
      cursor: default;
      pointer-events: none;
      touch-action: none;
    }
  `}
`;

const SlideWrapper = styled.div` {
    display: inline-block;
    vertical-align: middle;
    height: 100%;
  }
`;

const ArrowButton = styled(Icon)`
  min-width: 28px;
  color: ${(props) => (props.buttonDisabled
    ? props.theme.colors.shades[5]
    : props.theme.colors.shades[2])};
  cursor: ${(props) => (props.buttonDisabled ? 'default' : 'pointer')};
`;


const Carousel = (props) => {
  const sliderWrapperElement = useRef(null);
  const sliderFrameElement = useRef(null);

  const [currentSlideIndex, setCurrentSlideIndex] = useState(null);
  const [dragState, setDragState] = useState({
    dragStartPosition: null,
    dragEndPosition: null,
    // Possible phases: noMouseOrTouchInteraction, afterMouseOrTouchDown,
    // afterClickOrTouchWithoutDrag, afterSingleDrag, afterFinishingDragging
    dragPhase: 'noMouseOrTouchInteraction'
  });

  // helper functions which set CSS style for a DOM element
  const setStyle = (target, styles) => {
    Object.keys(styles).forEach((attribute) => {
      target.style[attribute] = styles[attribute];
    });
  };

  // initializes sizes of SliderWrapper and SliderFrame components
  const initializeSizesOfComponents = () => {
    const sliderWrapperElementWidth = sliderWrapperElement.current.getBoundingClientRect().width;
    const sliderFrameElementChildrenLength = sliderFrameElement.current.children.length;

    // calculates and set size slides frame
    setStyle(sliderFrameElement.current, {
      width: `${(sliderWrapperElementWidth) * sliderFrameElementChildrenLength}px`,
      transition: `all ${props.duration}ms ${props.easing}`
    });

    // calculates and set size of each slide in order to be the the same for all slides
    for (let i = 0; i < sliderFrameElementChildrenLength; i++) {
      setStyle(sliderFrameElement.current.children[i], {
        width: `${100 / sliderFrameElementChildrenLength}%`
      });
    }
  };

  // scrolls to slide
  const scrollToSlide = (slideNumber) => {
    const sliderWrapperElementWidth = sliderWrapperElement.current.getBoundingClientRect().width;
    sliderFrameElement.current.style.transform =
      `translate3d(-${slideNumber * (sliderWrapperElementWidth)}px, 0, 0)`;
  };

  // detects if it is necessary to scroll to another slide
  const updateCurrentSlide = () => {
    const sliderFrameElementChildrenLength = sliderFrameElement.current.children.length;
    const movement = dragState.dragEndPosition - dragState.dragStartPosition;

    if (movement > 0 && Math.abs(movement) > props.threshold) {
      // scrolling to previous slide if it is possible
      if (currentSlideIndex === 0) {
        if (props.loop) {
          setCurrentSlideIndex(sliderFrameElementChildrenLength - 1);
        } else {
          // scrolling to previous slide not possible so scroll back to the current slide
          scrollToSlide(currentSlideIndex);
        }
      } else {
        setCurrentSlideIndex(currentSlideIndex - 1);
      }
    } else if (movement < 0 && Math.abs(movement) > props.threshold) {
      // scrolling to next slide if it is possible
      if (currentSlideIndex === sliderFrameElementChildrenLength - 1) {
        if (props.loop) {
          setCurrentSlideIndex(0);
        } else {
          // scrolling to next slide not possible so scroll back to the current slide
          scrollToSlide(currentSlideIndex);
        }
      } else {
        setCurrentSlideIndex(currentSlideIndex + 1);
      }
    } else {
      // no movement or movement was shorter than threshold, so scroll back to currentSlide
      scrollToSlide(currentSlideIndex);
    }
  };

  // init of the component
  useEffect(() => {
    // init of component
    initializeSizesOfComponents();
    setCurrentSlideIndex(props.defaultSelectedSlideIndex);
  }, [props.defaultSelectedSlideIndex]);

  // when currentSlideIndex changes we need to scroll to the new slide with the index
  useEffect(() => {
    // init of component
    scrollToSlide(currentSlideIndex);
  }, [currentSlideIndex]);

  // create resize event listener
  useEffect(() => {
    const onResize = () => {
      initializeSizesOfComponents();
      scrollToSlide(currentSlideIndex);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  });


  // when state of dragging change
  useEffect(() => {
    if (dragState.dragPhase === 'noMouseOrTouchInteraction') {
      // user doesn't do any mouse or touch interaction with Carousel, so nothing to do
      return;
    }

    if (dragState.dragPhase === 'afterSingleDrag') {
      // user has performed single drag by moving a mouse or a finger
      // so we have to perform animation
      const sliderWrapperElementWidth = sliderWrapperElement.current.getBoundingClientRect().width;
      setStyle(sliderFrameElement.current, {
        cursor: 'grabbing',
        transition: `all 0ms ${props.easing}`,
        transform: `translate3d(${((currentSlideIndex *
          (sliderWrapperElementWidth)) +
          (dragState.dragStartPosition - dragState.dragEndPosition)) * -1}px, 0, 0)`
      });
    }

    if (dragState.dragPhase === 'afterFinishingDragging' ||
      dragState.dragPhase === 'afterClickOrTouchWithoutDrag') {

      if (dragState.dragPhase === 'afterFinishingDragging') {
        // user finished dragging so we need to perform animation and change slide if necessary
        setStyle(sliderFrameElement.current, {
          cursor: 'grab',
          transition: `all ${props.duration}ms ${props.easing}`
        });
        updateCurrentSlide();
      }

      // resetting dragState after end of dragging
      // or after user's click or touch without dragging
      setDragState({
        dragStartPosition: null,
        dragEndPosition: null,
        dragPhase: 'noMouseOrTouchInteraction'
      });
    }
  }, [dragState]);


  return (
    <ComponentWrapper className={props.className} data-qa={props.qa}>
      {props.disabled ? <CarouselDisabledOverlay /> : null}
      <CarouselWrapper disabled={props.disabled}>
        { props.showArrowButtons && props.children.length > 1 && (
          <ArrowButton
            buttonDisabled={!props.loop && currentSlideIndex === 0}
            icon={'ChevronRight'}
            rotate={180}
            size={'large'}
            onClick={(e) => {
              e.preventDefault();

              if (currentSlideIndex !== 0) {
                setCurrentSlideIndex(currentSlideIndex - 1);
              } else if (props.loop) {
                setCurrentSlideIndex(sliderFrameElement.current.children.length - 1);
              }
            }}
          />
        )}
        <SliderWrapper
          ref={sliderWrapperElement}
          onTouchStart={(e) => {
            const dragStartPosition = e.touches[0].pageX;
            setDragState((prevState) => ({
              ...prevState,
              dragStartPosition,
              dragPhase: 'afterMouseOrTouchDown'
            }));
          }}
          onTouchEnd={(e) => {
            if (dragState.dragEndPosition === null) {
              setDragState((prevState) => ({
                ...prevState,
                dragPhase: 'afterClickOrTouchWithoutDrag'
              }));
            } else {
              setDragState((prevState) => ({
                ...prevState,
                dragPhase: 'afterFinishingDragging'
              }));
            }
          }}
          onTouchMove={(e) => {
            if (dragState.dragPhase === 'afterMouseOrTouchDown' ||
              dragState.dragPhase === 'afterSingleDrag') {
              const dragEndPosition = e.touches[0].pageX;

              setDragState((prevState) => ({
                ...prevState,
                dragEndPosition,
                dragPhase: 'afterSingleDrag'
              }));
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();

            const dragStartPosition = e.pageX;
            setDragState((prevState) => ({
              ...prevState,
              dragStartPosition,
              dragPhase: 'afterMouseOrTouchDown'
            }));
          }}
          onMouseUp={(e) => {
            e.preventDefault();

            if (dragState.dragEndPosition === null) {
              setDragState((prevState) => ({
                ...prevState,
                dragPhase: 'afterClickOrTouchWithoutDrag'
              }));
            } else {
              setDragState((prevState) => ({
                ...prevState,
                dragPhase: 'afterFinishingDragging'
              }));
            }
          }}
          onMouseLeave={(e) => {
            e.preventDefault();

            if (dragState.dragPhase === 'afterSingleDrag') {
              const dragEndPosition = e.pageX;

              setDragState((prevState) => ({
                ...prevState,
                dragEndPosition,
                dragPhase: 'afterFinishingDragging'
              }));
            }
          }}
          onMouseMove={(e) => {
            e.preventDefault();
            if (dragState.dragPhase === 'afterMouseOrTouchDown' ||
              dragState.dragPhase === 'afterSingleDrag') {
              const dragEndPosition = e.pageX;

              setDragState((prevState) => ({
                ...prevState,
                dragEndPosition,
                dragPhase: 'afterSingleDrag'
              }));
            }
          }}
        >
          <SliderFrame
            ref={sliderFrameElement}
            draggingInProgress={dragState.dragPhase === 'afterSingleDrag'}
          >
            {Children.map(props.children, (child) => (
              <SlideWrapper>
                {child}
              </SlideWrapper>
            ))}
          </SliderFrame>
        </SliderWrapper>
        { props.showArrowButtons && props.children.length > 1 && (
          <ArrowButton
            buttonDisabled={!props.loop && currentSlideIndex === props.children.length - 1}
            icon={'ChevronRight'}
            rotate={0}
            size={'large'}
            onClick={(e) => {
              e.preventDefault();
              if (currentSlideIndex !== props.children.length - 1) {
                setCurrentSlideIndex(currentSlideIndex + 1);
              } else if (props.loop) {
                setCurrentSlideIndex(0);
              }
            }}
          />
        )}
      </CarouselWrapper>
      {props.showNavigation && props.children.length > 1 && (
        <CarouselNavigation
          currentNavigationItemIndex={currentSlideIndex}
          onNavigationItemClick={(navigationItemIndex) => {
            setCurrentSlideIndex(
              Math.min(
                Math.max(navigationItemIndex, 0),
                sliderFrameElement.current.children.length - 1
              )
            );
          }}
          numberOfNavigationItems={props.children.length}
        />
      )}
    </ComponentWrapper>
  );
};

Carousel.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]).isRequired,
  showNavigation: PropTypes.bool,
  showArrowButtons: PropTypes.bool,
  duration: PropTypes.number,
  easing: PropTypes.string,
  loop: PropTypes.bool,
  defaultSelectedSlideIndex: PropTypes.number,
  threshold: PropTypes.number,
  disabled: PropTypes.bool,
  qa: PropTypes.string,
  className: PropTypes.string
};

Carousel.defaultProps = {
  showNavigation: true,
  showArrowButtons: true,
  duration: 200,
  easing: 'ease-out',
  loop: false,
  defaultSelectedSlideIndex: 0,
  threshold: 20,
  disabled: false,
  qa: undefined,
  className: undefined
};

export default Carousel;
